import { Address } from '@/types/api/organisations.types';
import {
  BOQStatus,
  ClientInfo,
  ClientType,
  ContractTypeEnum,
  Funding,
  Site,
  UpdateSiteBody,
} from '@/types/api/sites.types';
import i18n from '@/plugins/vue-i18n';
import { CumulativeNetData } from '@/types/api/project.types';
import { getDateAndTimestamp } from '@/common/utils/date.utils';

const { t } = i18n.global;

export const copyAddress = (address?: Address): Address | undefined => {
  if (!address) return undefined;
  return { ...address };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getClientInfo = (siteValue: Site, isCompany: boolean): any => {
  const clientInfo = {
    clientName: siteValue.clientName,
    email: siteValue.clientEmail,
    contactNumber: siteValue.clientContactNumber,
    contactNumberCountryCode: siteValue.clientContactNumberCountryCode || '27',
    clientType: siteValue.clientType,
  } as ClientInfo;
  if (isCompany) {
    clientInfo.companyVat = siteValue.companyVAT;
    clientInfo.companyName = siteValue.companyName;
  }
  return clientInfo;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getFundingInfo = (siteValue: Site, type: string): any | undefined => {
  const funding = siteValue.fundings.find((funding: Funding) => funding.type === type);
  if (!funding) return undefined;
  if (type === 'Bank') {
    return {
      bankId: funding.bank.id,
      amount: funding.amount,
      accountNumber: funding.accountNumber,
      // No screen that sends this edits it, but the PUT clears it when left out, and an archived
      // site refuses any funding change, so unarchiving would fail.
      additionalDetails: funding.additionalDetails,
    };
  } else if (type === 'Self') {
    return {
      amount: funding.amount,
    };
  }
};

/**
 * The contract terms a site PUT must send back when it does not edit them. The PUT maps the whole
 * body onto the site, so an omitted target margin is cleared and a cost plus site without its markup
 * is rejected.
 */
export const unchangedContractTerms = (
  site: Site,
): Pick<UpdateSiteBody, 'targetMargin'> &
  Partial<Pick<UpdateSiteBody, 'defaultMarkup' | 'overrideItemMarkups'>> => ({
  // The API rejects any target margin on No Contract; null also clears a stale one.
  targetMargin:
    site.contractType === ContractTypeEnum.noContract ? null : (site.targetMargin ?? null),
  // Context: the API rejects a markup on anything but cost plus; `overrideItemMarkups: false` keeps
  // the BOQ's per-item markups as they are.
  ...(site.contractType === ContractTypeEnum.costPlus && site.defaultMarkup !== undefined
    ? { defaultMarkup: site.defaultMarkup, overrideItemMarkups: false }
    : {}),
});

export const archiveSiteData = (site: Site, archived: boolean): UpdateSiteBody => {
  const siteAddress = copyAddress(site.address);
  const deliveryAddress = copyAddress(site.deliveryAddress);

  const companyInfo = getClientInfo(site, true);
  const privateClientInfo = getClientInfo(site, false);

  const bankFundingInfo = getFundingInfo(site, 'Bank');
  const selfFundingInfo = getFundingInfo(site, 'Self');

  const data = {
    name: site.name,
    // Context: an archive never changes a date, but a Date round-trip can (null becomes 1970), and
    // the backend would treat that as a date change; send exactly what the server returned.
    startDate: site.startDate,
    endDate: site.endDate,
    siteStatusId: site.siteStatus.id,
    archived: !archived,
    clientInfo: site.clientType === ClientType.company ? companyInfo : privateClientInfo,
    fundingInfo: {},
    additionalInfo: {},
    siteShortCode: site.siteShortCode,
    erfNumber: site.erfNumber,
    ...unchangedContractTerms(site),
  } as UpdateSiteBody;

  if (siteAddress) {
    data.additionalInfo.address = siteAddress;
  }
  if (deliveryAddress) {
    data.additionalInfo.deliveryAddress = deliveryAddress;
  }

  if (bankFundingInfo) {
    data.fundingInfo.bankFundingInfo = bankFundingInfo;
  }

  if (selfFundingInfo) {
    data.fundingInfo.selfFundingInfo = selfFundingInfo;
  }

  return data;
};

export const getBOQStatusColor = (status: string): string => {
  let colors = '';

  switch (status) {
    case BOQStatus.draft:
      colors = 'text-txt-700';
      break;
    case BOQStatus.pending:
      colors = 'text-orange-600';
      break;
    case BOQStatus.approved:
      colors = 'text-semantic-success-green-600';
      break;
  }

  return colors;
};

export const getBOQStatusLabel = (status: string): string => {
  let label = '';

  switch (status) {
    case BOQStatus.draft:
      label = t('sites.boq.statuses.draft');
      break;
    case BOQStatus.pending:
      label = t('sites.boq.statuses.pending');
      break;
    case BOQStatus.approved:
      label = t('sites.boq.statuses.approved');
      break;
  }

  return label;
};

export const getContractTypeLabel = (contractType: ContractTypeEnum): string => {
  let label = '';

  switch (contractType) {
    case ContractTypeEnum.noContract:
      label = t('sites.boq.contractTypes.noContract');
      break;
    case ContractTypeEnum.costPlus:
      label = t('sites.boq.contractTypes.costPlus');
      break;
    case ContractTypeEnum.fixedCost:
      label = t('sites.boq.contractTypes.fixedCost');
      break;
  }

  return label;
};

export const getSiteStartEndDisplayText = (startDate: string, endDate: string): string => {
  return `${getDateAndTimestamp(startDate, false, false)} - ${getDateAndTimestamp(endDate, false, false)}`;
};

export const getFutureMonthIndexes = (data: CumulativeNetData[]): number[] => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;

  return data
    .map((entry, index) => {
      if (
        entry.year > currentYear ||
        (entry.year === currentYear && entry.monthNumber > currentMonth)
      ) {
        return index;
      }
      return null;
    })
    .filter((index): index is number => index !== null);
};
